import React, { useLayoutEffect } from 'react';
import './campaign-donation.scss';
import ParametersWidget from '../../common/Parameters';
import i18n from 'i18next';
import { animated } from '@react-spring/web';
import {
  formatAnimatedDonationNumber,
  DEFAULT_FONT,
  LanguageCode,
  loadGoogleFont,
  VISIBILITY_CODE,
  WIDGET_TYPE,
  getCurrentWidgetAmount,
} from '../../utils';
import { useTranslation } from 'react-i18next';
import { useWidgetData, useAnimatedNumber } from '../../hooks';
import { WidgetAPI } from '../../services';

const CampaignDonation = (props: any) => {
  const parameters: ParametersWidget = new ParametersWidget(window.location.search, WIDGET_TYPE.DONATION);
  const { t } = useTranslation('language', { keyPrefix: 'donation_widget' });
  var timeoutLanguage: any = null;
  const identifier = parameters.getIdentifier() || '';

  const customCampaignDonationQueryFn = async () => {
    try {
      const data = await WidgetAPI.getCampaignDonation(identifier);
      const { data: widget } = data;
      if (widget && Object.entries(widget?.result).length > 0) {
        return widget.result;
      }
      return null;
    } catch (error) {
      throw error;
    }
  };

  const { data: widgetData } = useWidgetData({
    identifier,
    enabled: !!identifier,
    queryFn: customCampaignDonationQueryFn,
  });

  const currentDonationAmount = getCurrentWidgetAmount(
    parameters,
    widgetData?.summary?.last_donation?.forest_size,
    widgetData?.summary?.last_donation?.amount,
    widgetData?.summary?.last_donation?.co2,
    widgetData,
  );

  // Use custom animated number hook for donation amount
  const spring = useAnimatedNumber(currentDonationAmount, {
    duration: 400,
    config: 'default',
  });

  const showDonorName = (donorName: string) => {
    if (donorName) {
      if (donorName === '.') {
        return t('txt_no_name_donor');
      } else {
        return donorName;
      }
    } else {
      return t('txt_anonymous');
    }
  };

  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    timeoutLanguage = setTimeout(() => {
      i18n.changeLanguage(parameters.getLanguageValue()?.toLowerCase() || LanguageCode.DE);
    }, 50);
    loadGoogleFont(parameters.getFontFamily() || DEFAULT_FONT);

    return () => {
      clearTimeout(timeoutLanguage);
    };
  }, [parameters.getLanguageValue(), parameters.getFontFamily()]);

  return (
    <div className="donation-widget">
      <div
        className="last-donation w-100 d-flex pb-24"
        style={{
          justifyContent: parameters.getAlignValue(),
          backgroundColor: `#${parameters.getBackgroundColor()}`,
          textAlign: parameters.getAlignValue() as React.CSSProperties['textAlign'],
        }}
      >
        <div className="widget">
          {parameters.getHeadlineValue() && (
            <h3
              className="m-0"
              style={{
                fontWeight: parameters.getHeadlineWeight(),
                color: `#${parameters.getTextColor()}`,
                fontSize: `${parameters.getFontSize()}px`,
                fontFamily: `'${parameters.getFontFamily()}', sans-serif`,
              }}
              dangerouslySetInnerHTML={{ __html: parameters.getHeadlineValue() as string }}
            ></h3>
          )}
          <div className="pt-10">
            {(!parameters.getVisibilityValue() || parameters.getVisibilityValue() === VISIBILITY_CODE.NAME) && (
              <span
                style={{
                  fontWeight: parameters.getDonorNameWeight(),
                  color: `#${parameters.getTextColor()}`,
                  fontSize: `${parameters.getFontSize()}px`,
                  fontFamily: `'${parameters.getFontFamily()}', sans-serif`,
                }}
              >
                {' '}
                {widgetData ? showDonorName(widgetData?.summary?.last_donation?.donor_name?.trim()) : 'N/A'}{' '}
              </span>
            )}

            {!parameters.getVisibilityValue() && (
              <span
                style={{
                  color: `#${parameters.getTextColor()}`,
                  fontSize: `${parameters.getFontSize()}px`,
                  fontFamily: `'${parameters.getFontFamily()}', sans-serif`,
                }}
              >
                {' - '}
              </span>
            )}
            {(!parameters.getVisibilityValue() || parameters.getVisibilityValue() === VISIBILITY_CODE.VALUE) && (
              <animated.span
                style={{
                  fontWeight: parameters.getValueWeight(),
                  color: `#${parameters.getTextColor()}`,
                  fontSize: `${parameters.getFontSize()}px`,
                  fontFamily: `'${parameters.getFontFamily()}', sans-serif`,
                }}
              >
                {spring.number.to((n: number) =>
                  formatAnimatedDonationNumber(n, parameters, widgetData?.summary?.last_donation, currentDonationAmount),
                )}
              </animated.span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignDonation;
